import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { toast } from "@redotech/redo-web/alert";
import { RedoFileUpload } from "@redotech/redo-web/arbiter-components/file-upload/redo-file-upload";
import { RedoTextareaInputField } from "@redotech/redo-web/arbiter-components/input/redo-textarea-input-field";
import { RedoModal } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { Flex } from "@redotech/redo-web/flex";
import { UploaderContext } from "@redotech/redo-web/image-upload";
import { Text } from "@redotech/redo-web/text";
import { memo, useState } from "react";
import { RedoOutboundLabelsRpcClientContext } from "../outbound-labels-rpc-client";

export const CreateSurchargeDisputeModal = memo(
  function CreateSurchargeDisputeModal({
    isOpen,
    onClose,
    onCreated,
    balanceTransactionId,
  }: {
    isOpen: boolean;
    onClose: () => void;
    onCreated: () => void;
    balanceTransactionId: string;
  }) {
    const rpcClient = useRequiredContext(RedoOutboundLabelsRpcClientContext);
    const uploader = useRequiredContext(UploaderContext);

    const [reason, setReason] = useState("");
    const [attachments, setAttachments] = useState<File[]>([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const handleClose = useHandler(() => {
      if (isSubmitting) {
        return;
      }
      setReason("");
      setAttachments([]);
      setError(null);
      onClose();
    });

    const reasonEmpty = !reason.trim();

    const handleSubmit = useHandler(async () => {
      if (reasonEmpty) {
        setError("Reason is required");
        return;
      }
      setIsSubmitting(true);
      setError(null);
      try {
        let attachmentUrls: string[] | null = null;
        if (attachments.length > 0) {
          const results = await Promise.all(
            attachments.map((file) =>
              uploader({ file, signal: AbortSignal.timeout(30_000) }),
            ),
          );
          attachmentUrls = results.map((r) => r.url.toString());
        }

        await rpcClient.createSurchargeDispute({
          balanceTransactionId,
          reason: reason.trim(),
          attachments: attachmentUrls,
        });
        toast("Dispute submitted successfully", { variant: "success" });
        setReason("");
        setAttachments([]);
        onCreated();
        onClose();
      } catch (e) {
        setError(
          e instanceof Error
            ? e.message
            : "Something went wrong. Please try again.",
        );
      } finally {
        setIsSubmitting(false);
      }
    });

    return (
      <RedoModal
        isOpen={isOpen}
        modalSize="md"
        onModalCloseRequested={handleClose}
        primaryButton={{
          text: "Submit",
          onClick: handleSubmit,
          disabled: isSubmitting || reasonEmpty,
          pending: isSubmitting,
          size: "sm",
        }}
        secondaryButton={{
          text: "Cancel",
          onClick: handleClose,
          disabled: isSubmitting,
          size: "sm",
        }}
        subtitle="What type adjustment is this?"
        title="Dispute adjustment"
      >
        <Flex dir="column" gap="xl" w="full">
          {error && (
            <Text fontSize="sm" textColor="error">
              {error}
            </Text>
          )}
          <RedoTextareaInputField
            fullWidth
            label="Reason"
            placeholder="Describe why this adjustment should be disputed..."
            setValue={setReason}
            value={reason}
          />
          <Flex dir="column" gap="xs">
            <RedoFileUpload
              acceptMessage="Provide any relevant documentation or evidence to support your dispute."
              files={attachments}
              fullWidth
              handleFileDeleted={(index) => {
                setAttachments(attachments.filter((_, i) => i !== index));
              }}
              handleFilesAdded={(files) => {
                if (files) {
                  setAttachments([...attachments, ...Array.from(files)]);
                }
              }}
              nFilesToAccept={5}
            />
          </Flex>
        </Flex>
      </RedoModal>
    );
  },
);
